// Live map (interface refresh, screen 3): map-first, with the vehicle list beside it.
// Two-line rows (6B), state as colour + shape (5C); picking a vehicle turns the side panel into its
// summary with Live | Journeys tabs (user request, instead of a card on the map), markers with Names / Group buttons. Sprout's version adds tracker problems and a
// link to the vehicle page; otherwise the same as web/src/pages/LiveMap.tsx.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, Vehicle } from '../api';
import { Location } from '../components/Address';
import { Flag } from '../components/Flag';
import { FridgeMark, Temperatures } from '../components/Temperatures';
import { FleetMap, MapVehicle, useMapEngine } from '../components/FleetMap';
import { Link } from '../components/Links';
import { mapState, MapState, STATE_LABEL, STATE_ORDER, StateIcon, StateWord } from '../components/VehicleState';
import { VehicleHeader, VehicleTabs } from '../components/VehicleTabs';
import { ago, duration, km, kmh, time, UNIT_STATUS_LABEL } from '../format';
import { useLiveFeed } from '../live';
import { GeofenceCard, useGeofenceDraft } from '../components/GeofenceCard';
import { useGeofences, useGeofencesShown } from '../components/geofenceStore';

function useTick(ms: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

// Second line of a row: what matters for that state.
function rowDetail(v: Vehicle, state: MapState, now: number): string {
  const parts: string[] = [];
  if (state === 'moving') parts.push(kmh(v.position?.speedKmh));
  else if (v.since) parts.push(`${STATE_LABEL[state]} ${duration((now - new Date(v.since).getTime()) / 60_000)}`);
  if (v.lastReceived) parts.push(ago(v.lastReceived, now));
  if (v.todayKm) parts.push(`today ${km(v.todayKm)}`);
  return parts.join(' · ');
}

export function LiveMap() {
  const fences = useGeofences();
  const [showFences] = useGeofencesShown();
  // Drawing a geofence in place (the Geofences card); Google only.
  const geo = useGeofenceDraft();
  const engine = useMapEngine();
  const editing = geo.draft?.id ?? null;
  const fencesShown = useMemo(() => (editing === null ? fences : fences.filter((f) => f.id !== editing)), [fences, editing]);
  const [vehicles, setVehicles] = useState<Vehicle[] | null>(null);
  const [error, setError] = useState('');
  // ?vehicle=ID (from a unit page or the Routes tab) opens with that vehicle picked and followed.
  const [params] = useSearchParams();
  const linked = Number(params.get('vehicle')) || null;
  const [selectedId, setSelectedId] = useState<number | null>(linked);
  const [follow, setFollow] = useState(linked !== null);
  const [query, setQuery] = useState('');
  const [only, setOnly] = useState<MapState | null>(null);
  // Bumped to make the map zoom back out to the whole fleet.
  const [fitRound, setFitRound] = useState(0);
  const now = useTick(30_000);
  const reloadTimer = useRef<number>();

  const load = useCallback(async () => {
    try {
      setVehicles((await api.vehicles()).vehicles);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  // Journey changes arrive in bursts; reload once they settle.
  const reloadSoon = useCallback(() => {
    window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => void load(), 1500);
  }, [load]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  useLiveFeed({
    onPosition: (p) =>
      setVehicles((list) => list?.map((v) => v.id !== p.vehicleId ? v : {
        ...v,
        lastReceived: new Date().toISOString(),
        position: { ...(v.position ?? { valid: true, satellites: null, powerV: null }), t: p.t, lat: p.lat, lon: p.lon, speedKmh: p.speedKmh, course: p.course },
      }) ?? list),
    onJourneys: reloadSoon,
    onResync: reloadSoon,
  });

  const rows = useMemo(() => (vehicles ?? []).map((v) => ({ v, state: mapState(v) }))
    .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state) || a.v.name.localeCompare(b.v.name)), [vehicles]);
  const counts = useMemo(() => rows.reduce<Partial<Record<MapState, number>>>((c, r) => ({ ...c, [r.state]: (c[r.state] ?? 0) + 1 }), {}), [rows]);
  const squash = (s: string) => s.toLowerCase().replace(/[-\s]/g, '');
  const words = query.split(/\s+/).map(squash).filter(Boolean);
  const shown = rows.filter(({ v, state }) => (!only || state === only)
    && words.every((w) => squash(`${v.name} ${v.registration ?? ''} ${v.customer?.name ?? ''}`).includes(w)));

  const onMap: MapVehicle[] = useMemo(
    () => rows.filter(({ v }) => v.position).map(({ v, state }) => ({
      id: v.id, name: v.name, state, lat: v.position!.lat, lon: v.position!.lon, course: v.position!.course,
      detail: state === 'moving' ? kmh(v.position!.speedKmh) : undefined,
    })),
    [rows],
  );
  const selected = rows.find((r) => r.v.id === selectedId) ?? null;
  // Opened with a vehicle picked: follow it instead of first fitting the whole fleet.
  const skipFleetFit = fitRound === 0 && linked !== null && onMap.some((v) => v.id === linked);

  const select = (id: number) => {
    setSelectedId(id);
    setFollow(true);
  };
  const close = () => {
    setSelectedId(null);
    setFollow(false);
  };

  // The picked vehicle's summary, in the side panel under the Live | Journeys tabs.
  const summary = selected && (
    <section className="vsum" aria-label={`${selected.v.name} summary`}>
      <VehicleTabs vehicleId={selected.v.id} active="live" />
      <VehicleHeader vehicle={selected.v} onBack={() => { close(); setFitRound((n) => n + 1); }}
        sub={[selected.v.registration, selected.v.customer?.name].filter(Boolean).join(' · ') || undefined} />
      <div className="vsum-state">
        <StateWord state={selected.state} />
        {selected.v.since && <span className="muted small">for {duration((now - new Date(selected.v.since).getTime()) / 60_000)}</span>}
        <label className="check small"><input id="follow" type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow on map</label>
      </div>
      <dl className="vsum-tiles">
        <div><dt>Speed</dt><dd>{kmh(selected.v.position?.speedKmh)}</dd></div>
        <div><dt>Today</dt><dd>{km(selected.v.todayKm)}</dd></div>
        <div><dt>This journey</dt><dd>{selected.v.currentJourneyKm !== null ? km(selected.v.currentJourneyKm) : '–'}</dd></div>
        <div><dt>Last report</dt><dd>{selected.v.lastReceived ? ago(selected.v.lastReceived, now) : '–'}</dd></div>
      </dl>
      <dl className="vsum-facts">
        <dt>Location</dt>
        <dd>{selected.v.country && <Flag country={selected.v.country.code} name={selected.v.country.name} />} {selected.v.position
          ? <Location lat={selected.v.position.lat} lon={selected.v.position.lon} /> : '–'}</dd>
        <dt>Reported at</dt><dd>{selected.v.lastReceived ? time(selected.v.lastReceived) : '–'}</dd>
        <dt>Voltage</dt><dd>{selected.v.position?.powerV != null ? `${selected.v.position.powerV.toFixed(1)} V` : '–'}</dd>
        <dt>Satellites</dt><dd>{selected.v.position?.satellites ?? '–'}</dd>
        {selected.v.trackerStatus && selected.v.trackerStatus !== 'ok' && <><dt>Tracker</dt><dd className="warn">{UNIT_STATUS_LABEL[selected.v.trackerStatus]}</dd></>}
      </dl>
      <Temperatures vehicleId={selected.v.id} compact refreshKey={[...selected.v.temperatures.map((t) => t.at), selected.v.fridge?.on, selected.v.fridge?.at].join()} />
      <div className="vsum-actions">
        <Link to={`/vehicles/${selected.v.id}`} state={{ from: `/?vehicle=${selected.v.id}`, fromLabel: 'Live map' }} className="button ghost small">Vehicle page</Link>
      </div>
    </section>
  );

  return (
    <div className="split live">
      <aside className="panel">
        {summary}
        <div hidden={!!summary} className="vlist-wrap">
        <div className="panel-head">
          <div className="live-title">
            <h1>Vehicles</h1>
            <span className="muted small">{vehicles === null ? 'Loading…' : `${rows.length} in total`}</span>
          </div>
          <input type="search" placeholder="Search name, registration, customer" aria-label="Search vehicles" value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="state-filter" role="group" aria-label="Show only">
            <button type="button" className={only === null ? 'on' : ''} aria-pressed={only === null} onClick={() => setOnly(null)}>All <span>{rows.length}</span></button>
            {STATE_ORDER.filter((s) => counts[s]).map((s) => (
              <button key={s} type="button" className={only === s ? 'on' : ''} aria-pressed={only === s} onClick={() => setOnly(only === s ? null : s)}>
                <StateIcon state={s} size={14} />{STATE_LABEL[s]} <span>{counts[s]}</span>
              </button>
            ))}
          </div>
          {error && <p className="error" role="alert">{error}</p>}
        </div>
        <ul className="vlist">
          {shown.map(({ v, state }) => (
            <li key={v.id}>
              <button className={`vrow2${v.id === selectedId ? ' on' : ''}`} onClick={() => (v.id === selectedId ? close() : select(v.id))} aria-pressed={v.id === selectedId}>
                <StateIcon state={state} course={v.position?.course ?? null} size={22} />
                <span className="sr-only">{STATE_LABEL[state]}</span>
                <span className="vrow2-main">
                  <span className="vrow2-top">
                    <span className="vrow2-name">{v.name}{v.registration && v.registration !== v.name && <small className="vrow2-reg">{v.registration}</small>}</span>
                    <span className="vrow2-icons">
                      {v.temperatures.filter((t) => t.state === 'high' || t.state === 'low').slice(0, 1).map((t) => (
                        <span key={t.name} className="temp-warn" title={`${t.name}: ${t.state === 'high' ? 'too warm' : 'too cold'}`}>{t.value?.toFixed(1)} {t.unit}</span>
                      ))}
                      {v.trackerStatus && ['nogps', 'nopower'].includes(v.trackerStatus) && <span className="tiny-warn" title={UNIT_STATUS_LABEL[v.trackerStatus]}>!</span>}
                      <FridgeMark fridge={v.fridge} />
                      {v.country && <Flag country={v.country.code} name={`In ${v.country.name}`} />}
                    </span>
                  </span>
                  <span className="vrow2-sub">{rowDetail(v, state, now)}</span>
                </span>
              </button>
            </li>
          ))}
          {vehicles !== null && shown.length === 0 && <li className="muted pad">No vehicles match.</li>}
        </ul>
        </div>
      </aside>
      <div className="map-wrap">
        <FleetMap vehicles={onMap} selectedId={selectedId} follow={follow && !geo.draft} onSelect={select} markerOptions
          geofences={fencesShown} showGeofences={showFences || !!geo.draft} drawing={geo.drawing} goTo={geo.goTo}
          fitKey={vehicles && !skipFleetFit ? `fleet-${fitRound}` : undefined} ready={vehicles !== null || !!error}
        />
        <div className="map-overlays">
          {!geo.draft && <details className="map-key">
            <summary>Key</summary>
            <ul>{STATE_ORDER.map((s) => <li key={s}><StateIcon state={s} size={18} />{STATE_LABEL[s]}</li>)}</ul>
            <p className="muted small">The arrow points the way a moving vehicle is heading. <b>Names</b> and <b>Group</b> (top right) show the names or bundle vehicles that are close together; click a number to zoom in.</p>
          </details>}
          <GeofenceCard draft={geo.draft} setDraft={geo.setDraft} setGoTo={geo.setGoTo} canDraw={engine !== null && engine !== 'osm'} />
        </div>
      </div>
    </div>
  );
}
